import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, ArrowUpDown, Download, FileText, Loader2, Receipt, Search,
} from 'lucide-react';
import ReportDownloadLog from '@/components/ReportDownloadLog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const DOC_TYPE_OPTIONS = [
  { value: 'all', label: 'All Documents' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'delivery_challan', label: 'Delivery Challan' },
];

const DATE_PRESET_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'custom', label: 'Custom range' },
];

const ORDER_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending', label: 'Pending' },
];

const SORTABLE_COLUMNS = [
  { key: 'document_date', label: 'Date' },
  { key: 'document_number', label: 'Invoice/DC' },
  { key: 'order_number', label: 'Order #' },
  { key: 'document_type', label: 'Doc Type' },
  { key: 'franchise_name', label: 'Franchise' },
  { key: 'product_name', label: 'Product' },
];

const formatINR = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : '0.00';

const formatQty = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '0';

const docTypeBadge = (type) => {
  if (type === 'Invoice') {
    return (
      <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-700">
        <Receipt size={12} className="mr-1" /> Invoice
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-700">
      <FileText size={12} className="mr-1" /> Delivery Challan
    </Badge>
  );
};

const SummaryCard = ({ label, value, accent, testid }) => (
  <Card className={`border-2 ${accent}`} data-testid={testid}>
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </CardContent>
  </Card>
);

const SalesFulfillmentReport = () => {
  const navigate = useNavigate();

  // ── filter state ──────────────────────────────────────────────
  const [filters, setFilters] = useState({
    document_type: 'all',
    date_preset: 'all',
    date_from: '',
    date_to: '',
    order_status: 'all',
    warehouse: 'all',
    franchise: '',
    business_segment: 'all',
    search: '',
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  // ── data ──────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total_invoice_documents: 0,
    total_dc_documents: 0,
    total_invoice_value: 0,
    total_dc_value: 0,
    total_sales_documents: 0,
    total_quantity_dispatched: 0,
  });
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [logRefresh, setLogRefresh] = useState(0);

  const reqIdRef = useRef(0);
  const searchTimerRef = useRef(null);

  // ── query-param builder ───────────────────────────────────────
  const buildParams = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      const pass = (k, v) => {
        if (v && v !== 'all' && v !== '') p.set(k, v);
      };
      pass('document_type', filters.document_type);
      if (filters.date_preset && filters.date_preset !== 'all') {
        pass('date_preset', filters.date_preset);
        if (filters.date_preset === 'custom') {
          pass('date_from', filters.date_from);
          pass('date_to', filters.date_to);
        }
      }
      pass('order_status', filters.order_status);
      pass('warehouse', filters.warehouse);
      pass('franchise', filters.franchise);
      pass('business_segment', filters.business_segment);
      pass('search', filters.search);
      Object.entries(extra).forEach(([k, v]) => p.set(k, v));
      return p;
    },
    [filters],
  );

  // ── network calls ─────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoadingRows(true);
    try {
      const token = localStorage.getItem('token');
      const params = buildParams({
        sort_by: sortBy,
        sort_dir: sortDir,
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      const { data } = await axios.get(`${API}/reports/sales-fulfillment?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (reqId !== reqIdRef.current) return; // ignore stale response
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to load report rows');
    } finally {
      if (reqId === reqIdRef.current) setLoadingRows(false);
    }
  }, [buildParams, sortBy, sortDir, page]);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const token = localStorage.getItem('token');
      const params = buildParams();
      const { data } = await axios.get(`${API}/reports/sales-fulfillment/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
    } catch {
      // soft-fail summary; rows take precedence
    } finally {
      setLoadingSummary(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchRows();
    fetchSummary();
  }, [fetchRows, fetchSummary]);

  // Reset to page 1 whenever any filter changes (except `page` itself)
  useEffect(() => {
    setPage(1);
  }, [
    filters.document_type, filters.date_preset, filters.date_from, filters.date_to,
    filters.order_status, filters.warehouse, filters.franchise,
    filters.business_segment, filters.search,
    sortBy, sortDir,
  ]);

  // ── filter handlers ───────────────────────────────────────────
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const onSearchChange = (e) => {
    const v = e.target.value;
    setFilters((prev) => ({ ...prev, search: v }));
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    // debounce-driven re-query happens automatically through `useEffect`,
    // but we throttle state updates so we don't hammer it on every keystroke.
    searchTimerRef.current = setTimeout(() => {
      /* state already set — effect picks it up */
    }, 250);
  };

  const onSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  // ── export ────────────────────────────────────────────────────
  const downloadExport = async (kind /* 'xlsx' | 'pdf' */) => {
    setDownloading(kind);
    try {
      const token = localStorage.getItem('token');
      const params = buildParams({ sort_by: sortBy, sort_dir: sortDir });
      const { data } = await axios.get(
        `${API}/reports/sales-fulfillment.${kind}?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `sales_fulfillment_${stamp}.${kind}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Sales Fulfillment report exported (${kind.toUpperCase()})`);
      setLogRefresh((n) => n + 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : `Failed to download ${kind.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  // ── pagination helpers ────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageInfo = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`;
  }, [page, total]);

  // ── render ────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="sales-fulfillment-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            data-testid="sf-back-btn"
          >
            <ArrowLeft size={16} className="mr-1" />
            Reports
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Receipt className="text-blue-600" size={26} />
              Sales Fulfillment Report
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Every Invoice and Delivery Challan in one consolidated grid.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => downloadExport('xlsx')}
            disabled={!!downloading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sf-export-xlsx"
          >
            {downloading === 'xlsx'
              ? <Loader2 size={16} className="animate-spin mr-2" />
              : <Download size={16} className="mr-2" />}
            Excel
          </Button>
          <CsvDownloadButton onDownload={() => downloadExport('xlsx')} testId="sf-export-csv" size="default" />
          <Button
            onClick={() => downloadExport('pdf')}
            disabled={!!downloading}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="sf-export-pdf"
          >
            {downloading === 'pdf'
              ? <Loader2 size={16} className="animate-spin mr-2" />
              : <FileText size={16} className="mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* ─── KPI cards ─── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sf-summary">
        <SummaryCard label="Invoice Docs" value={summary.total_invoice_documents.toLocaleString()}
          accent="border-blue-200 bg-blue-50/40" testid="sf-kpi-invoice-docs" />
        <SummaryCard label="DC Docs" value={summary.total_dc_documents.toLocaleString()}
          accent="border-violet-200 bg-violet-50/40" testid="sf-kpi-dc-docs" />
        <SummaryCard label="Total Sales Docs" value={summary.total_sales_documents.toLocaleString()}
          accent="border-slate-200 bg-slate-50/40" testid="sf-kpi-total-docs" />
        <SummaryCard label="Invoice Value (₹)" value={formatINR(summary.total_invoice_value)}
          accent="border-blue-200 bg-blue-50/40" testid="sf-kpi-invoice-value" />
        <SummaryCard label="DC Value (₹)" value={formatINR(summary.total_dc_value)}
          accent="border-violet-200 bg-violet-50/40" testid="sf-kpi-dc-value" />
        <SummaryCard label="Total Qty Dispatched" value={formatQty(summary.total_quantity_dispatched)}
          accent="border-emerald-200 bg-emerald-50/40" testid="sf-kpi-total-qty" />
      </div>

      {/* ─── Filter bar ─── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Document Type</Label>
              <Select value={filters.document_type} onValueChange={(v) => setFilter('document_type', v)}>
                <SelectTrigger data-testid="sf-filter-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPE_OPTIONS.map((o) =>
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date Range</Label>
              <Select value={filters.date_preset} onValueChange={(v) => setFilter('date_preset', v)}>
                <SelectTrigger data-testid="sf-filter-date-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATE_PRESET_OPTIONS.map((o) =>
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Order Status</Label>
              <Select value={filters.order_status} onValueChange={(v) => setFilter('order_status', v)}>
                <SelectTrigger data-testid="sf-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORDER_STATUS_OPTIONS.map((o) =>
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Warehouse</Label>
              <Input
                placeholder="e.g. Main Warehouse"
                value={filters.warehouse === 'all' ? '' : filters.warehouse}
                onChange={(e) => setFilter('warehouse', e.target.value || 'all')}
                data-testid="sf-filter-warehouse"
              />
            </div>
            <div>
              <Label className="text-xs">Franchise</Label>
              <Input
                placeholder="ID or name"
                value={filters.franchise}
                onChange={(e) => setFilter('franchise', e.target.value)}
                data-testid="sf-filter-franchise"
              />
            </div>
            <div>
              <Label className="text-xs">Business Segment</Label>
              <Input
                placeholder="e.g. Retail"
                value={filters.business_segment === 'all' ? '' : filters.business_segment}
                onChange={(e) => setFilter('business_segment', e.target.value || 'all')}
                data-testid="sf-filter-segment"
              />
            </div>
          </div>

          {filters.date_preset === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={filters.date_from}
                  onChange={(e) => setFilter('date_from', e.target.value)}
                  data-testid="sf-filter-date-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={filters.date_to}
                  onChange={(e) => setFilter('date_to', e.target.value)}
                  data-testid="sf-filter-date-to" />
              </div>
            </div>
          )}

          <div className="relative">
            <Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              placeholder="Search by Invoice/DC/Order #, Franchise, Product, Barcode…"
              value={filters.search}
              onChange={onSearchChange}
              className="pl-8"
              data-testid="sf-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* ─── Grid ─── */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sf-grid">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {SORTABLE_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className="px-3 py-2 text-left font-semibold text-slate-700 cursor-pointer select-none whitespace-nowrap"
                      onClick={() => onSort(c.key === 'document_date' ? 'date' : c.key)}
                      data-testid={`sf-sort-${c.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <ArrowUpDown size={12} className="text-slate-400" />
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Customer</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Barcode</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">HSN</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">GST</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Batch</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Qty</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Unit</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">MRP</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Batch MRP</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">PO MRP</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">GRN MRP</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Master MRP</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Inward B2B (Incl GST)</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Selling</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Margin</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Margin %</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Discount</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Before Taxable Value</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Tax Value</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">Net</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Warehouse</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Created By</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Order Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 whitespace-nowrap">Dispatch</th>
                </tr>
              </thead>
              <tbody>
                {loadingRows && (
                  <tr>
                    <td colSpan={32} className="px-3 py-12 text-center text-slate-500" data-testid="sf-loading">
                      <Loader2 size={20} className="animate-spin inline mr-2" />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loadingRows && rows.length === 0 && (
                  <tr>
                    <td colSpan={32} className="px-3 py-12 text-center text-slate-500" data-testid="sf-empty">
                      No transactions match the current filters.
                    </td>
                  </tr>
                )}
                {!loadingRows && rows.map((r, i) => (
                  <tr key={`${r.document_type}-${r.document_number}-${i}`}
                      className="border-b border-slate-100 hover:bg-slate-50/50"
                      data-testid={`sf-row-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{r.document_date}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium">{r.document_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.order_number || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{docTypeBadge(r.document_type)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.franchise_name || <span className="text-slate-400">—</span>}</div>
                      {r.franchise_id && <div className="text-xs text-slate-500">{r.franchise_id}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div>{r.product_name || <span className="text-slate-400">—</span>}</div>
                      {r.product_id && <div className="text-xs text-slate-500">{r.product_id}</div>}
                    </td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.barcode}</td>
                    <td className="px-3 py-2 font-mono text-xs" data-testid={`sf-hsn-${i}`}>{r.hsn}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-gst-${i}`}>{r.gst}</td>
                    <td className="px-3 py-2">{r.batch_number}</td>
                    <td className="px-3 py-2 text-right">{formatQty(r.quantity)}</td>
                    <td className="px-3 py-2">{r.unit}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.mrp)}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-batch-mrp-${i}`}>{r.batch_mrp != null ? formatINR(r.batch_mrp) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-po-mrp-${i}`}>{r.po_mrp != null ? formatINR(r.po_mrp) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-grn-mrp-${i}`}>{r.grn_mrp != null ? formatINR(r.grn_mrp) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-master-mrp-${i}`}>{r.current_master_mrp != null ? formatINR(r.current_master_mrp) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-inward-b2b-${i}`}>{r.inward_b2b_incl_gst != null ? formatINR(r.inward_b2b_incl_gst) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.selling_price)}</td>
                    <td className={"px-3 py-2 text-right font-medium " + ((r.margin_amount ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700')} data-testid={`sf-margin-${i}`}>{r.margin_amount != null ? formatINR(r.margin_amount) : <span className="text-slate-400">—</span>}</td>
                    <td className={"px-3 py-2 text-right " + ((r.margin_pct ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700')} data-testid={`sf-margin-pct-${i}`}>{r.margin_pct != null ? r.margin_pct.toFixed(1) + '%' : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-right">{formatINR(r.discount)}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-taxable-${i}`}>{formatINR(r.before_taxable_value)}</td>
                    <td className="px-3 py-2 text-right" data-testid={`sf-taxvalue-${i}`}>{formatINR(r.tax_amount)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{formatINR(r.net_amount)}</td>
                    <td className="px-3 py-2">{r.warehouse}</td>
                    <td className="px-3 py-2">{r.created_by}</td>
                    <td className="px-3 py-2">{r.current_order_status}</td>
                    <td className="px-3 py-2">{r.dispatch_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Pagination footer ─── */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
            <div className="text-xs text-slate-600" data-testid="sf-page-info">
              {loadingSummary ? 'Updating…' : pageInfo}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm"
                disabled={page <= 1 || loadingRows}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="sf-prev"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-600">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline" size="sm"
                disabled={page >= totalPages || loadingRows}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="sf-next"
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ReportDownloadLog reportKey="sales-fulfillment" refreshToken={logRefresh} />
    </div>
  );
};

export default SalesFulfillmentReport;
